// Picture-in-Picture mini player: the scene is drawn into its own 16:9 canvas, piped through captureStream() into a
// hidden <video>, and that video floats in the browser's always-on-top PiP window.
// Chrome keeps requestAnimationFrame running for a page that owns a PiP window (also in a background tab or a
// minimised window), so the app's render loop keeps feeding it; it asks `due(now)` and then calls `present(...)`.
// The PiP window's play/pause button follows the <video>: playing sends the page's Media Session 'pause' action,
// paused sends 'play'. So the video has to mirror the music (setPaused), which freezes the picture while paused.
import { W, H, OUT_W, OUT_H } from './core.js';
import { createPacer } from './display.js';

const FPS = 30;                                   // a calm background window doesn't need the full 60

export const pipSupported = () =>
  Boolean(document.pictureInPictureEnabled && HTMLVideoElement.prototype.requestPictureInPicture && HTMLCanvasElement.prototype.captureStream);

export function createPip({ onChange = () => {} } = {}) {
  let canvas = null, ctx = null, video = null, stream = null, active = false, paused = false;
  const pace = createPacer(FPS);                  // the screen's pacer (site/js/engine/display.js), at 30

  function setup() {
    canvas = document.createElement('canvas');
    canvas.width = OUT_W; canvas.height = OUT_H;   // whole pixels (4x): stays crisp when the window scales it down
    ctx = canvas.getContext('2d', { alpha: false });
    video = document.createElement('video');
    video.className = 'pip-source';
    video.muted = true; video.playsInline = true; video.disableRemotePlayback = true;
    video.setAttribute('aria-hidden', 'true');
    video.addEventListener('enterpictureinpicture', () => { active = true; onChange(true); });
    video.addEventListener('leavepictureinpicture', () => { teardown(); onChange(false); });
    document.body.append(video);
  }
  function teardown() {
    active = false;
    if (stream) stream.getTracks().forEach(t => t.stop());
    stream = null;
    if (video) video.srcObject = null;
  }

  /** draw scene A, optionally cross-fading to scene B by `mix` (0..1) */
  function present(bufA, bufB = null, mix = 0) {
    ctx.imageSmoothingEnabled = false;
    ctx.globalAlpha = 1;
    ctx.drawImage(bufA, 0, 0, W, H, 0, 0, OUT_W, OUT_H);
    if (bufB && mix > 0) {
      ctx.globalAlpha = mix;
      ctx.drawImage(bufB, 0, 0, W, H, 0, 0, OUT_W, OUT_H);
      ctx.globalAlpha = 1;
    }
  }

  return {
    get active() { return active; },
    /** opens the window showing `buf`; must start inside a click or key press (browsers require a gesture).
     *  Resolves true once it's open, false if it already was (or is still opening). */
    async open(buf) {
      if (stream) return false;
      if (!video) setup();
      stream = canvas.captureStream();
      video.srcObject = stream;
      present(buf);                                // the first frame gives the video its size
      try {
        await video.play();
        if (video.readyState < 1) await new Promise(r => video.addEventListener('loadedmetadata', r, { once: true }));
        await video.requestPictureInPicture();
        if (paused) video.pause();
        return true;
      } catch (e) {
        teardown();
        throw e;
      }
    },
    close() {
      if (document.pictureInPictureElement === video) document.exitPictureInPicture().catch(() => {});
    },
    /** music paused: freeze the picture, so the PiP window shows a play button that resumes it */
    setPaused(p) {
      paused = p;
      if (!active) return;
      if (p) video.pause();
      else video.play().catch(() => {});
    },
    /** whether the render loop should present a frame at rAF time `now` (every other one at 60 Hz, every fourth at
     *  120: createPacer, which must see every frame while the window is open); `fps`: fewer still, where the screen
     *  gets fewer (Settings' Motion) */
    due(now, fps = FPS) {
      return active && !paused && pace(now, Math.min(FPS, fps));
    },
    present,
  };
}
